/* eslint-disable */
import { useCallback, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import HexagonProgress from "v2/features/premium-finance/pending-proposal/components/HexagonProgress";

export interface CollateralGapBarRow {
  planYear: number;
  year: number;
  insuredAge: number;
  surrenderValue: number | null;
  outsideAssets: number | null;
  financingBalance: number | null;
  difference: number | null;
  status: string | null;
  percentage: number | null;
}

interface CollateralGapChartProps {
  data: CollateralGapBarRow[];
  isDark: boolean;
  loading?: boolean;
}

interface ChipPosition {
  x: number;
  leftX: number;
  rightX: number;
  y: number;
  idx: number;
}

const SURRENDER_COLOR = "#3B82F6";
const OUTSIDE_COLOR = "#93C5FD";
const FINANCING_COLOR = "#94A3B8";
const SHORTFALL_COLOR = "#E24C4C";
const SURPLUS_COLOR = "#1EA97C";
const PRIMARY = "#1E3A8A";
const INITIAL_VISIBLE = 5;

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(v));

const fmtAxis = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${v / 1_000}K`;
  return `$${v}`;
};

function CollateralGapChart({ data, isDark, loading }: CollateralGapChartProps) {
  const echartsRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<{ start: number; end: number } | null>(null);

  const [chips, setChips] = useState<ChipPosition[]>([]);
  const [hoveredChipIdx, setHoveredChipIdx] = useState<number | null>(null);

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";

    const useInitialWindow = !zoomRef.current;
    const initialEndIdx = Math.min(INITIAL_VISIBLE - 1, Math.max(data.length - 1, 0));

    return {
      animation: false,
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: 60, right: 24, top: 56, bottom: 60 },
      dataZoom: [
        {
          type: "slider",
          show: true,
          startValue: useInitialWindow ? 0 : undefined,
          endValue: useInitialWindow ? initialEndIdx : undefined,
          start: !useInitialWindow ? zoomRef.current?.start ?? 0 : undefined,
          end: !useInitialWindow
            ? zoomRef.current?.end ?? Math.min(100, (INITIAL_VISIBLE / (data.length || 1)) * 100)
            : undefined,
          minValueSpan: 1,
          height: 24,
          bottom: 4,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#334155",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: true,
          showDataShadow: true,
          realtime: true,
          filterMode: "filter",
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category",
        data: data.map((r) => `Anniversary\n${r.year}`),
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11, lineHeight: 16 },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: textColor, fontSize: 11, formatter: fmtAxis },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
      },
      series: [
        // ── Left group: stacked surrender + outside assets ───────────────────
        {
          name: "Surrender Value",
          type: "bar",
          stack: "collateral",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.surrenderValue ?? 0),
          itemStyle: { color: SURRENDER_COLOR },
        },
        {
          name: "Outside Assets",
          type: "bar",
          stack: "collateral",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.outsideAssets ?? 0),
          itemStyle: { color: OUTSIDE_COLOR, borderRadius: [3, 3, 0, 0] },
        },
        // ── Right bar: financing balance ─────────────────────────────────────
        {
          name: "Financing Balance",
          type: "bar",
          barMaxWidth: 52,
          barCategoryGap: "25%",
          barGap: "15%",
          data: data.map((r) => r.financingBalance ?? 0),
          itemStyle: { color: FINANCING_COLOR, borderRadius: [3, 3, 0, 0] },
        },
      ],
    };
  }, [data, isDark]);

  // ── Chip recomputation ───────────────────────────────────────────────────────

  const recomputeChips = useCallback(() => {
    const chart = echartsRef.current?.getEchartsInstance?.();
    if (!chart) return;

    const currentYear = new Date().getFullYear();
    const newChips: ChipPosition[] = [];

    data.forEach((row, i) => {
      if (row.year > currentYear) return;
      const leftTotal = (row.surrenderValue ?? 0) + (row.outsideAssets ?? 0);
      const rightTotal = row.financingBalance ?? 0;
      if (leftTotal <= 0 && rightTotal <= 0) return;

      const leftPx = chart.convertToPixel({ seriesIndex: 0 }, [i, leftTotal]) as
        | [number, number]
        | null;
      const rightPx = chart.convertToPixel({ seriesIndex: 2 }, [i, rightTotal]) as
        | [number, number]
        | null;

      if (!leftPx || !rightPx) return;

      const chipX = (leftPx[0] + rightPx[0]) / 2;
      const chipY = Math.min(leftPx[1], rightPx[1]);

      newChips.push({ x: chipX, leftX: leftPx[0], rightX: rightPx[0], y: chipY, idx: i });
    });

    setChips(newChips);
  }, [data]);

  // ── Chart events ─────────────────────────────────────────────────────────────

  const chartEvents = useMemo(
    () => ({
      finished: recomputeChips,
      dataZoom: (params: any) => {
        zoomRef.current = { start: params.start, end: params.end };
        recomputeChips();
      },
    }),
    [recomputeChips],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="relative w-full">
      <ReactECharts
        ref={echartsRef}
        option={option}
        style={{ height: 320, cursor: "default" }}
        notMerge
        lazyUpdate
        showLoading={loading}
        loadingOption={{
          text: "",
          color: PRIMARY,
          maskColor: isDark ? "rgba(30,32,48,0.8)" : "rgba(255,255,255,0.8)",
        }}
        onEvents={chartEvents}
      />

      {/* ── Floating chips ────────────────────────────────────────────────── */}
      {chips.map((chip) => {
        const row = data[chip.idx];
        if (!row) return null;

        const isSurplus =
          row.status === "surplus" || (row.difference != null && row.difference >= 0);
        const accentColor = isSurplus ? SURPLUS_COLOR : SHORTFALL_COLOR;
        const pct = row.percentage != null ? Math.round(Math.abs(row.percentage) * 100) : null;
        const diff = row.difference ?? 0;
        const innerFill = isSurplus ? "#A0E6BA" : "#fae0d4";

        return (
          <div
            key={chip.idx}
            style={{
              position: "absolute",
              left: chip.x,
              top: chip.y - 8,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                pointerEvents: "auto",
                zIndex: 10,
                position: "relative",
                cursor: "pointer",
              }}
              onMouseEnter={() => setHoveredChipIdx(chip.idx)}
              onMouseLeave={() => setHoveredChipIdx(null)}
            >
              <div
                style={{
                  width: 66,
                  height: 55,
                  flexShrink: 0,
                  position: "absolute",
                  left: "-40px",
                  top: -5,
                }}
              >
                <HexagonProgress
                  percentage={pct ?? 0}
                  size={66}
                  strokeWidth={10}
                  progressColor={accentColor}
                  backgroundColor={innerFill}
                  centerFill={isSurplus ? "#E8F8EF" : "#FEF3EF"}
                  textColor={accentColor}
                  status="inProgress"
                />
              </div>

              {/* Label box */}
              <div
                style={{
                  padding: "2px 10px",
                  border: `2px solid ${accentColor}`,
                  borderRadius: 8,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  paddingLeft: "24px",
                }}
              >
                <span
                  style={{ color: isDark ? "#94A3B8" : "#64748B", fontSize: 16, fontWeight: 600 }}
                >
                  {isSurplus ? "Surplus" : "Shortfall"}:
                </span>
                <span
                  style={{
                    color: accentColor,
                    fontSize: 16,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    fontFamily: "Conthrax Semi-Bold",
                  }}
                >
                  {isSurplus ? `+${fmt(Math.abs(diff))}` : `-${fmt(Math.abs(diff))}`}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Chip hover tooltip ───────────────────────────────────────────── */}
      {hoveredChipIdx !== null &&
        (() => {
          const chip = chips.find((c) => c.idx === hoveredChipIdx);
          const row = data[hoveredChipIdx];
          if (!chip || !row) return null;
          const isSurplus =
            row.status === "surplus" || (row.difference != null && row.difference >= 0);
          const accentColor = isSurplus ? SURPLUS_COLOR : SHORTFALL_COLOR;
          const diff = row.difference ?? 0;
          const sepColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(156,163,175,0.4)";
          const rowBorder = isDark ? "#4A4A60" : "#E5E7EB";
          const flipBelow = chip.y < 180;
          return (
            <div
              style={{
                position: "absolute",
                left: chip.x,
                top: flipBelow ? chip.y + 8 : chip.y - 16,
                transform: flipBelow ? "translate(-50%, 0)" : "translate(-50%, calc(-100% - 8px))",
                zIndex: 50,
                pointerEvents: "none",
                background: isDark ? "#2A2A3D" : "#ffffff",
                border: `1px solid ${isDark ? "#3A3A50" : "#E5E7EB"}`,
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                overflow: "hidden",
                minWidth: 230,
              }}
            >
              <div
                style={{
                  background: isDark ? "#3A3A50" : "#E5E7EB",
                  color: isDark ? "#fff" : "#374151",
                  padding: "8px 12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 12,
                  fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                  fontWeight: "bold",
                  fontSize: 12,
                }}
              >
                <span>{row.year}</span>
                <span
                  style={{ width: 1, height: 20, background: sepColor, display: "inline-block" }}
                />
                <span>Plan Yr {row.planYear}</span>
                <span
                  style={{ width: 1, height: 20, background: sepColor, display: "inline-block" }}
                />
                <span>A{row.insuredAge}</span>
              </div>
              <div style={{ padding: "8px 12px" }}>
                {(
                  [
                    { label: "Surrender Value", color: SURRENDER_COLOR, value: row.surrenderValue },
                    { label: "Outside Assets", color: OUTSIDE_COLOR, value: row.outsideAssets },
                    {
                      label: "Financing Balance",
                      color: FINANCING_COLOR,
                      value: row.financingBalance,
                    },
                  ] as { label: string; color: string; value: number | null }[]
                ).map(({ label, color, value }) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      marginBottom: 2,
                      borderBottom: `1px solid ${rowBorder}`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          color: isDark ? "#fff" : "#374151",
                          fontSize: 12,
                          fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                        }}
                      >
                        {label}
                      </span>
                    </div>
                    <span
                      style={{
                        color: isDark ? "#fff" : "#374151",
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 12,
                        fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                      }}
                    >
                      {value != null ? fmt(value) : "—"}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 8px 2px",
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      color: accentColor,
                      fontSize: 12,
                      fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                    }}
                  >
                    {isSurplus ? "Surplus" : "Shortfall"}
                  </span>
                  <span
                    style={{
                      color: accentColor,
                      fontSize: 13,
                      fontWeight: 700,
                      marginLeft: 12,
                      fontFamily: "'Conthrax Semi-Bold', Arial, sans-serif",
                    }}
                  >
                    {isSurplus ? `+${fmt(Math.abs(diff))}` : `-${fmt(Math.abs(diff))}`}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

export default CollateralGapChart;
